import React from 'react'
import { formatActivityTime, formatActivityDate, getActivityStatusText, getActivityDescription } from '../../utils/activityFormat'

export interface ActivityItemData {
  id: string
  medicationId: string
  medicationName: string
  scheduledTime?: string
  actualTime?: Date | any
  timestamp?: Date | any
  type?: string
  status?: string
  stockBefore?: number
  stockAfter?: number
  notes?: string
}

interface ActivityItemProps {
  activity: ActivityItemData
  compact?: boolean
  showUndo?: boolean
  onUndo?: (activityId: string) => void
}

/**
 * Reusable component for rendering a medication activity item
 */
export const ActivityItem: React.FC<ActivityItemProps> = ({ 
  activity, 
  compact = false,
  showUndo = false,
  onUndo
}) => {
  const getActivityIcon = () => {
    const type = activity.type || activity.status || ''
    
    if (type === 'medication_taken' || activity.status === 'taken') {
      const sizeClass = compact ? 'w-6 h-6' : 'w-10 h-10'
      const iconSize = compact ? 'w-3 h-3' : 'w-5 h-5'
      return (
        <div className={`${sizeClass} bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center`}>
          <svg className={iconSize + ' text-green-600 dark:text-green-400'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      )
    } else if (type === 'stock_adjustment' || activity.status === 'stock_adjusted') {
      const sizeClass = compact ? 'w-6 h-6' : 'w-10 h-10'
      const iconSize = compact ? 'w-3 h-3' : 'w-5 h-5'
      return (
        <div className={`${sizeClass} bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center`}>
          <svg className={iconSize + ' text-blue-600 dark:text-blue-400'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )
    } else if (activity.status === 'missed') {
      const sizeClass = compact ? 'w-6 h-6' : 'w-10 h-10'
      const iconSize = compact ? 'w-3 h-3' : 'w-5 h-5'
      return (
        <div className={`${sizeClass} bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center`}>
          <svg className={iconSize + ' text-red-600 dark:text-red-400'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      )
    }

    // Default icon
    const sizeClass = compact ? 'w-6 h-6' : 'w-10 h-10'
    const iconSize = compact ? 'w-3 h-3' : 'w-5 h-5'
    return (
      <div className={`${sizeClass} bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center`}>
        <svg className={iconSize + ' text-gray-600 dark:text-gray-400'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    )
  }

  // Get the actual date object for formatting
  const getActualDate = (): Date => {
    const timestamp = activity.actualTime || activity.timestamp
    if (!timestamp) return new Date()
    
    if (typeof timestamp === 'object' && 'toDate' in timestamp && timestamp.toDate) {
      return timestamp.toDate()
    } else if (typeof timestamp === 'object' && 'seconds' in timestamp && timestamp.seconds) {
      return new Date(timestamp.seconds * 1000)
    } else if (timestamp instanceof Date) {
      return timestamp
    }
    return new Date(timestamp)
  }

  const actualDate = getActualDate()

  if (compact) {
    return (
      <div className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
        {getActivityIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {activity.medicationName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatActivityTime(actualDate)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      {getActivityIcon()}

      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 dark:text-white text-sm">
          {activity.medicationName}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {getActivityDescription(activity)}
        </div>
        {activity.notes && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {activity.notes}
          </div>
        )}
      </div>

      {showUndo && (activity.type === 'medication_taken' || activity.status === 'taken') && onUndo && (
        <button
          onClick={() => onUndo(activity.id)}
          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm px-3 py-1 border border-red-300 dark:border-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          Deshacer
        </button>
      )}
    </div>
  )
}

export default ActivityItem

